import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '@/api';
import { errorCode, errorMessage, isErrorCode } from '@/api/errors';
import type { components } from '@/api/qraft/schema';
import { RadioButtons } from '@/common/forms/RadioButtons';
import { WBSButton } from '@/common/WBSButton';
import { CheckboxLabel, WBSCheckbox } from '@/common/WBSCheckbox';
import {
  Divider,
  FormField,
  StyledInput,
  StyledTextarea,
} from '~/styles/forms/formElements';
import { SelectGroup } from '~/styles/forms/styledSelectGroup';
import { Heading2, Heading3 } from '~/styles/forms/text';
import { formatString } from '~/utils/stringFormat';

export const Route = createFileRoute('/study-helper-application')({
  component: StudyHelperApplicationPage,
  beforeLoad: () => {
    return {
      pageBackground: 'white',
      noContainer: true,
    };
  },
});

type ApiCountry = components['schemas']['Country'];
type ApiLanguage = components['schemas']['Language'];
type AgreementResponse = components['schemas']['AgreementResponse'];
type AgreementKey =
  | 'accept_wbs_agreement'
  | 'faithful_membership'
  | 'teaching_consistency'
  | 'use_followup_system'
  | 'volunteer_acknowledgment';

interface FormData {
  // Personal info
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  address_1: string;
  address_2: string;
  city: string;
  state: string;
  zip: string;
  country: ApiCountry | '';
  birthdate: string;
  gender: 'male' | 'female' | '';
  preferred_languages: ApiLanguage[];

  // Congregation info
  congregation_name: string;
  congregation_address_1: string;
  congregation_address_2: string;
  congregation_city: string;
  congregation_state: string;
  congregation_zip: string;
  congregation_country: ApiCountry | '';
  congregation_website: string;

  // Radio responses
  accept_wbs_agreement: AgreementResponse | '';
  faithful_membership: AgreementResponse | '';
  teaching_consistency: AgreementResponse | '';
  use_followup_system: AgreementResponse | '';
  volunteer_acknowledgment: AgreementResponse | '';

  // Other
  comments_motivation: string;
  referral_source: string;
}

const initialFormData: FormData = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  address_1: '',
  address_2: '',
  city: '',
  state: '',
  zip: '',
  country: '',
  birthdate: '',
  gender: '',
  preferred_languages: [],

  congregation_name: '',
  congregation_address_1: '',
  congregation_address_2: '',
  congregation_city: '',
  congregation_state: '',
  congregation_zip: '',
  congregation_country: '',
  congregation_website: '',

  accept_wbs_agreement: '',
  faithful_membership: '',
  teaching_consistency: '',
  use_followup_system: '',
  volunteer_acknowledgment: '',

  comments_motivation: '',
  referral_source: '',
};

const LANGUAGES: { code: ApiLanguage; name: string }[] = [
  { code: 'EN', name: 'English' },
  { code: 'ES', name: 'Spanish' },
  { code: 'FR', name: 'French' },
  { code: 'PT_BR', name: 'Portuguese (Brazil)' },
];

/**
 * The five agreement statements in legacy's field order, using the migrated
 * catalogue text. The keys are `radio_responses`' keys, which the admin
 * reviewer console renders.
 *
 * Two statements are numbered "4.": legacy hid one of them on the public form.
 * All five are shown because all five reach the reviewer, and the catalogue
 * text isn't renumbered.
 */
const AGREEMENT_QUESTIONS = [
  { key: 'accept_wbs_agreement', labelKey: 'wbs.1-i-have-read' },
  { key: 'faithful_membership', labelKey: 'wbs.2-i-am-a' },
  { key: 'teaching_consistency', labelKey: 'wbs.3-i-agree-to' },
  { key: 'use_followup_system', labelKey: 'wbs.4-i-will-use' },
  { key: 'volunteer_acknowledgment', labelKey: 'lessons.4-as-a-wbs-2' },
  // `as const` and not an annotation: `t()` is keyed on the literal union of
  // every catalogue key, so a widened `labelKey: string` does not type-check.
] as const satisfies readonly { key: AgreementKey; labelKey: string }[];

function StudyHelperApplicationPage() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  /**
   * The generated client, not `fetch`: it sends the `x-wbs-language` header,
   * which the server stores as `submitted_language` for the approval email, and
   * it type-checks the request body.
   */
  const {
    mutateAsync: submitApplication,
    isPending,
    error,
    reset: clearSubmitError,
  } = api.submitHelperApplication.useMutation();

  // Countries come localized from the server as a closed list; legacy's
  // free-text country fields led to miscoded countries.
  const { data: countryData } = api.getCountries.useQuery();
  const countryOptions = countryData?.countries ?? [];

  /**
   * `wbs.i-agree` and `wbs.i-disagree` are the keys the follow-up applications
   * use for the same control. `wbs.i-m-not-sure` was added from the migrated
   * catalogue.
   */
  const AGREEMENT_OPTIONS = [
    { value: 'agree', label: t('wbs.i-agree') },
    { value: 'disagree', label: t('wbs.i-disagree') },
    { value: 'not_sure', label: t('wbs.i-m-not-sure') },
  ];

  const problemMessage = t('wbs.there-was-a-problem-with-your-submission');

  /**
   * The banner text branches on the error code, since both refusals carry a
   * code and no message. The already-pending copy reuses the follow-up
   * applications' key, whose text is role-generic.
   */
  const refusalMessage = ((): string => {
    if (isErrorCode(error, 'user_already_exists')) {
      // Legacy also refuses an existing email at submit, not at approval.
      return formatString(
        t('notifications.the-email-address-s'),
        formData.email.trim(),
      );
    }
    if (isErrorCode(error, 'application_already_exists')) {
      return t('followUp.application.already-pending');
    }
    return errorMessage(error, problemMessage);
  })();

  const updateField = <K extends keyof FormData>(
    field: K,
    value: FormData[K],
  ) => {
    // The mutation's `error` outlives the request, so clear it on edit;
    // otherwise the banner stays up while the applicant fixes the field it's
    // about, most likely the email.
    if (error) clearSubmitError();
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const toggleLanguage = (langCode: ApiLanguage) => {
    setFormData((prev) => ({
      ...prev,
      preferred_languages: prev.preferred_languages.includes(langCode)
        ? prev.preferred_languages.filter((l) => l !== langCode)
        : [...prev.preferred_languages, langCode],
    }));
  };

  const allRadiosAnswered = AGREEMENT_QUESTIONS.every(
    ({ key }) => formData[key] !== '',
  );

  /**
   * The form's required fields, which match `HelperApplicationCreateRequest`'s
   * non-optional ones.
   */
  const isFormValid =
    formData.first_name.trim() !== '' &&
    formData.last_name.trim() !== '' &&
    formData.email.trim() !== '' &&
    formData.phone.trim() !== '' &&
    formData.city.trim() !== '' &&
    formData.state.trim() !== '' &&
    formData.country !== '' &&
    formData.birthdate !== '' &&
    formData.gender !== '' &&
    formData.preferred_languages.length > 0 &&
    formData.congregation_name.trim() !== '' &&
    formData.congregation_address_1.trim() !== '' &&
    formData.congregation_city.trim() !== '' &&
    formData.congregation_state.trim() !== '' &&
    formData.congregation_country !== '' &&
    allRadiosAnswered;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) return;

    // The same checks as `isFormValid`, restated so the compiler narrows the
    // literal-union fields away from their empty options.
    const {
      country,
      congregation_country,
      gender,
      accept_wbs_agreement,
      faithful_membership,
      teaching_consistency,
      use_followup_system,
      volunteer_acknowledgment,
    } = formData;
    if (
      country === '' ||
      congregation_country === '' ||
      gender === '' ||
      accept_wbs_agreement === '' ||
      faithful_membership === '' ||
      teaching_consistency === '' ||
      use_followup_system === '' ||
      volunteer_acknowledgment === ''
    ) {
      return;
    }

    try {
      await submitApplication({
        body: {
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          phone: formData.phone,
          address_1: formData.address_1,
          address_2: formData.address_2 || null,
          city: formData.city,
          state: formData.state,
          zip: formData.zip || null,
          country,
          birthdate: formData.birthdate,
          gender,
          preferred_languages: formData.preferred_languages,
          congregation_name: formData.congregation_name,
          congregation_address_1: formData.congregation_address_1,
          congregation_address_2: formData.congregation_address_2 || null,
          congregation_city: formData.congregation_city,
          congregation_state: formData.congregation_state,
          congregation_zip: formData.congregation_zip || null,
          congregation_country,
          congregation_website: formData.congregation_website || null,
          radio_responses: {
            accept_wbs_agreement,
            faithful_membership,
            teaching_consistency,
            use_followup_system,
            volunteer_acknowledgment,
          },
          comments_motivation: formData.comments_motivation || null,
          referral_source: formData.referral_source || null,
          // `has_existing_account` is never sent; the server refuses it.
          // `submitted_language` comes from the `x-wbs-language` header.
        },
      });
      setSubmitSuccess(true);
      window.scrollTo({ top: 0 });
    } catch {
      // Shown from the mutation's `error` below; caught only to avoid an
      // unhandled rejection.
    }
  };

  if (submitSuccess) {
    return (
      <PageWrapper>
        <Container>
          <Title>{t('lessons.study-helper-application')}</Title>
          {/* The catalogue's own confirmation text and markup. It tells
             applicants not to register on the website or app, which would
             create a student account and delay approval. */}
          <SuccessMessage
            data-testid="application-confirmation"
            // biome-ignore lint/security/noDangerouslySetInnerHtml: bundled catalogue copy, not user input -- the <h2>/<h3>/<a> markup is the migrated string's own
            dangerouslySetInnerHTML={{
              __html: t('notifications.thank-you-for-applying'),
            }}
          />
        </Container>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <Container>
        <Title>{t('lessons.study-helper-application')}</Title>
        <Form onSubmit={handleSubmit}>
          {/* Personal Information */}
          <FormField>
            <Heading2>{t('wbs.personal-information')}</Heading2>
            <GridContainer>
              <div>
                <Heading3>
                  {t('wbs.first-name')} <Required>*</Required>
                </Heading3>
                <StyledInput
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={(e) => updateField('first_name', e.target.value)}
                  required
                />
              </div>
              <div>
                <Heading3>
                  {t('wbs.last-name')} <Required>*</Required>
                </Heading3>
                <StyledInput
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={(e) => updateField('last_name', e.target.value)}
                  required
                />
              </div>
              <div>
                <Heading3>
                  {t('notifications.email')} <Required>*</Required>
                </Heading3>
                <StyledInput
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  required
                />
              </div>
              <div>
                <Heading3>
                  {t('wbs.phone-2')} <Required>*</Required>
                </Heading3>
                <StyledInput
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => updateField('phone', e.target.value)}
                  required
                />
              </div>
              <div>
                <Heading3>
                  {t('wbs.birthdate')} <Required>*</Required>
                </Heading3>
                <StyledInput
                  type="date"
                  name="birthdate"
                  value={formData.birthdate}
                  onChange={(e) => updateField('birthdate', e.target.value)}
                  required
                />
              </div>
              <div>
                <Heading3>
                  {t('wbs.gender')} <Required>*</Required>
                </Heading3>
                <SelectGroup>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={(e) =>
                      updateField(
                        'gender',
                        e.target.value as FormData['gender'],
                      )
                    }
                    required
                  >
                    {/* There's no translated "Select Gender", so this uses
                       the generic `wbs.select` placeholder; the label
                       above names the field. */}
                    <option value="">{t('wbs.select')}</option>
                    <option value="male">{t('wbs.male')}</option>
                    <option value="female">{t('wbs.female')}</option>
                  </select>
                  <span className="icon">
                    <FontAwesomeIcon icon={faCaretDown} />
                  </span>
                </SelectGroup>
              </div>
            </GridContainer>
          </FormField>

          <Divider />

          {/* Address */}
          <FormField>
            <Heading2>{t('actions.address-2')}</Heading2>
            <GridContainer>
              <div style={{ gridColumn: '1 / -1' }}>
                <Heading3>{t('actions.address-i-optional')}</Heading3>
                <StyledInput
                  type="text"
                  name="address_1"
                  value={formData.address_1}
                  onChange={(e) => updateField('address_1', e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <Heading3>{t('actions.address-ii-optional')}</Heading3>
                <StyledInput
                  type="text"
                  name="address_2"
                  value={formData.address_2}
                  onChange={(e) => updateField('address_2', e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <Heading3>
                  {t('wbs.city')} <Required>*</Required>
                </Heading3>
                <StyledInput
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={(e) => updateField('city', e.target.value)}
                  required
                />
              </div>
              <div>
                <Heading3>
                  {t('wbs.state-province')} <Required>*</Required>
                </Heading3>
                <StyledInput
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={(e) => updateField('state', e.target.value)}
                  required
                />
              </div>
              <div>
                <Heading3>{t('forum.zip-postal')}</Heading3>
                <StyledInput
                  type="text"
                  name="zip"
                  value={formData.zip}
                  onChange={(e) => updateField('zip', e.target.value)}
                />
              </div>
              <div>
                <Heading3>
                  {t('wbs.country')} <Required>*</Required>
                </Heading3>
                <SelectGroup>
                  <select
                    name="country"
                    value={formData.country}
                    onChange={(e) =>
                      updateField(
                        'country',
                        e.target.value as FormData['country'],
                      )
                    }
                    required
                  >
                    <option value="">{t('wbs.select-country')}</option>
                    {countryOptions.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  <span className="icon">
                    <FontAwesomeIcon icon={faCaretDown} />
                  </span>
                </SelectGroup>
              </div>
            </GridContainer>
          </FormField>

          <Divider />

          {/* Preferred Languages */}
          <FormField>
            <Heading2>
              {t('wbs.preferred-languages')} <Required>*</Required>
            </Heading2>
            <CheckboxGroup>
              {LANGUAGES.map((lang) => (
                <CheckboxLabel key={lang.code}>
                  <WBSCheckbox
                    checked={formData.preferred_languages.includes(lang.code)}
                    onChange={() => toggleLanguage(lang.code)}
                  />
                  {lang.name}
                </CheckboxLabel>
              ))}
            </CheckboxGroup>
          </FormField>

          <Divider />

          {/* Congregation Information */}
          <FormField>
            <Heading2>{t('wbs.congregation-information')}</Heading2>
            <GridContainer>
              <div style={{ gridColumn: '1 / -1' }}>
                <Heading3>
                  {t('wbs.congregation-name')} <Required>*</Required>
                </Heading3>
                <StyledInput
                  type="text"
                  name="congregation_name"
                  value={formData.congregation_name}
                  onChange={(e) =>
                    updateField('congregation_name', e.target.value)
                  }
                  style={{ width: '100%' }}
                  required
                />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <Heading3>
                  {t('actions.congregation-address-i')} <Required>*</Required>
                </Heading3>
                <StyledInput
                  type="text"
                  name="congregation_address_1"
                  value={formData.congregation_address_1}
                  onChange={(e) =>
                    updateField('congregation_address_1', e.target.value)
                  }
                  style={{ width: '100%' }}
                  required
                />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <Heading3>
                  {t('actions.congregation-address-ii-optional')}
                </Heading3>
                <StyledInput
                  type="text"
                  name="congregation_address_2"
                  value={formData.congregation_address_2}
                  onChange={(e) =>
                    updateField('congregation_address_2', e.target.value)
                  }
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <Heading3>
                  {t('wbs.city')} <Required>*</Required>
                </Heading3>
                <StyledInput
                  type="text"
                  name="congregation_city"
                  value={formData.congregation_city}
                  onChange={(e) =>
                    updateField('congregation_city', e.target.value)
                  }
                  required
                />
              </div>
              <div>
                <Heading3>
                  {t('wbs.state-province')} <Required>*</Required>
                </Heading3>
                <StyledInput
                  type="text"
                  name="congregation_state"
                  value={formData.congregation_state}
                  onChange={(e) =>
                    updateField('congregation_state', e.target.value)
                  }
                  required
                />
              </div>
              <div>
                <Heading3>{t('forum.zip-postal')}</Heading3>
                <StyledInput
                  type="text"
                  name="congregation_zip"
                  value={formData.congregation_zip}
                  onChange={(e) =>
                    updateField('congregation_zip', e.target.value)
                  }
                />
              </div>
              <div>
                <Heading3>
                  {t('wbs.country')} <Required>*</Required>
                </Heading3>
                <SelectGroup>
                  <select
                    name="congregation_country"
                    value={formData.congregation_country}
                    onChange={(e) =>
                      updateField(
                        'congregation_country',
                        e.target.value as FormData['congregation_country'],
                      )
                    }
                    required
                  >
                    <option value="">{t('wbs.select-country')}</option>
                    {countryOptions.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  <span className="icon">
                    <FontAwesomeIcon icon={faCaretDown} />
                  </span>
                </SelectGroup>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <Heading3>{t('wbs.6-please-enter-your')}</Heading3>
                <StyledInput
                  type="url"
                  name="congregation_website"
                  value={formData.congregation_website}
                  onChange={(e) =>
                    updateField('congregation_website', e.target.value)
                  }
                  placeholder="https://"
                  style={{ width: '100%' }}
                />
              </div>
            </GridContainer>
          </FormField>

          <Divider />

          {/* Radio Questions */}
          <FormField>
            <Heading2>
              {/* Translation Needed */}
              Please indicate your agreement with the following statements{' '}
              <Required>*</Required>
            </Heading2>

            {AGREEMENT_QUESTIONS.map(({ key, labelKey }) => (
              <RadioQuestion key={key}>
                <QuestionText>{t(labelKey)}</QuestionText>
                <RadioButtons
                  name={key}
                  options={AGREEMENT_OPTIONS}
                  value={formData[key]}
                  onChange={(value) =>
                    updateField(key, value as AgreementResponse)
                  }
                />
              </RadioQuestion>
            ))}
          </FormField>

          <Divider />

          {/* Additional Information */}
          <FormField>
            {/* Translation Needed */}
            <Heading2>Additional Information</Heading2>
            <div style={{ marginBottom: '1rem' }}>
              {/* Translation Needed */}
              <Heading3>
                Why do you want to become a Study Helper? (Optional)
              </Heading3>
              <StyledTextarea
                name="comments_motivation"
                rows={4}
                value={formData.comments_motivation}
                onChange={(e) =>
                  updateField('comments_motivation', e.target.value)
                }
                // Translation Needed
                placeholder="Tell us about your motivation..."
              />
            </div>
            <div>
              <Heading3>{t('wbs.8-who-referred-you')}</Heading3>
              <StyledInput
                type="text"
                name="referral_source"
                value={formData.referral_source}
                onChange={(e) => updateField('referral_source', e.target.value)}
                style={{ width: '100%' }}
              />
            </div>
          </FormField>

          {error && (
            <ErrorMessage
              data-testid="application-error"
              data-error-code={errorCode(error)}
            >
              {refusalMessage}
            </ErrorMessage>
          )}

          <WBSButton type="submit" disabled={!isFormValid || isPending}>
            {/* Not translated: there's no catalogue string for these.
               Legacy's button said Submit (`actions.submit`), but the e2e
               spec selects on "Submit Application", so renaming it means
               changing the spec too. */}
            {isPending ? 'Submitting...' : 'Submit Application'}
          </WBSButton>
        </Form>
      </Container>
    </PageWrapper>
  );
}

const PageWrapper = styled.div`
  background: var(--grey-100);
  min-height: 70vh;
  padding: 40px 20px;
`;

const Container = styled.div`
  background: var(--white);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin: 0 auto;
  max-width: 800px;
  padding: 45px 60px;

  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

const Title = styled.h1`
  color: var(--grey-900);
  font-family: var(--raleway);
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 40px;
  text-align: center;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const GridContainer = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: 1fr 1fr;

  & > div {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const RadioQuestion = styled.div`
  margin-bottom: 1.5rem;
`;

const QuestionText = styled.p`
  color: var(--grey-800);
  font-family: var(--raleway);
  font-size: 14px;
  font-weight: 400;
  margin-bottom: 0.75rem;
`;

const Required = styled.span`
  color: var(--wbs-red);
`;

const ErrorMessage = styled.div`
  background-color: #fee2e2;
  border: 1px solid #ef4444;
  border-radius: 6px;
  color: #dc2626;
  font-family: var(--raleway);
  font-size: 14px;
  margin-top: 1rem;
  padding: 12px 16px;
`;

const SuccessMessage = styled.div`
  color: var(--grey-700);
  font-family: var(--raleway);
  font-size: 16px;
  line-height: 1.6;
  text-align: center;
`;
